/**
 * Seeker / Provider translation strings. Two-locale bundle. No PII copy anywhere.
 * Keep 6th-8th grade reading level; expand acronyms.
 */
export const STRINGS = {
  en: {
    seeker_title: "Find a housing program that fits your situation.",
    seeker_lede: "This is a directory of what exists in Bexar County. It is not an application. To actually get help, we'll connect you to Coordinated Entry, 2-1-1, or the agency that runs the program.",
    seeker_step: "Answer three quick things — no name, no account.",
    situation_label: "What is your situation?",
    where_label: "What ZIP or area? (optional)",
    household_label: "Who is with you?",
    find_button: "Show programs",
    switch_lang: "Español",
    switch_lang_alt: "Cambiar a español",
    crisis_title: "If you or someone with you is in danger tonight",
    handoffs_title: "How to actually get help",
    handoff_call: "Call",
    handoff_web: "Website",
    programs_title: "Programs that may fit",
    empty_msg: "We didn't find a listed program for this exact situation. That does not mean help isn't available — the routes above are the fastest way in.",
    who_it_serves: "Who this helps",
    what_pays: "What it pays for",
    wait: "How long people usually wait",
    documents: "Bring these if you can",
    action_label: "Get connected",
    action_help: "Call the number above, or ask Coordinated Entry for a referral. You do not need to fill out anything on this website to get help.",
    empty_situation: "Not sure — just show me what exists",
    situations: {
      veteran: "Veteran",
      recovery: "Leaving treatment or in recovery",
      reentry: "Leaving jail or prison",
      dv: "Fleeing violence at home",
      disability: "Living with a disability",
      youth: "Young adult (18–24) without stable housing",
      senior: "Older adult (55+ or 62+)",
      priced_out: "Priced out — I work but can't afford rent",
      not_sure: "Not sure",
    },
    households: {
      just_me: "Just me",
      me_children: "Me and children",
      couple: "A couple",
      larger: "Larger household",
    },
    provider_title: "I have a unit. What could it be?",
    provider_lede: "See which housing programs your property could serve — without listing it, publishing it, or creating an account. Nothing is shared until you say so.",
    provider_address: "Property address",
    provider_zip: "ZIP code (optional)",
    provider_beds: "Bedrooms",
    provider_baths: "Bathrooms",
    provider_sqft: "Square footage",
    provider_occupied: "Currently occupied?",
    provider_submit: "Show program fit",
    owner_form_asking_about: "Asking about",
    provider_fits_title: "Programs that could use this property",
    provider_must_be_true: "What would have to be true",
    provider_next: "Talk to Throughline",
    owner_next_heading: "Want the full picture for this property?",
    owner_next_body: "A person will review this specific property and send back what each option would actually pay, what the property would need, and how it compares to renting it conventionally. Typical reply is within two business days.",
    owner_next_button: "Get the full picture",
    owner_form_title: "Send your property to Throughline",
    owner_form_property: "For",
    owner_form_name: "Your name",
    owner_form_email: "Email",
    owner_form_email_help: "We send a one-time confirmation link here, then a person replies to this address.",
    owner_err_email: "Enter a valid email address — it's how we confirm and reply to you.",
    owner_err_name: "Please enter your name.",
    owner_form_phone: "Phone (optional)",
    owner_form_org: "Organization (optional)",
    owner_form_notes: "Anything else about the property? (optional)",
    owner_form_submit: "Send",
    owner_sent: "Thanks — we have your property. Check your email for a quick confirmation link, then a person will review it and reply within two business days. Your property has not been listed or published.",
    privacy_note: "Your property is not listed, not published, not searchable by others. Nothing is shared without you.",
    footer_compliance: "Throughline is informational infrastructure. It is not affiliated with HUD, the VA, or Bexar County. It is not an application, waitlist, or intake.",
  },
  es: {
    seeker_title: "Encuentre un programa de vivienda para su situación.",
    seeker_lede: "Este es un directorio de lo que existe en el Condado de Bexar. No es una solicitud. Para recibir ayuda de verdad, lo conectaremos con la Entrada Coordinada (Coordinated Entry), 2-1-1, o la agencia que administra el programa.",
    seeker_step: "Responda tres preguntas rápidas — sin nombre, sin cuenta.",
    situation_label: "¿Cuál es su situación?",
    where_label: "¿Cuál código postal o área? (opcional)",
    household_label: "¿Quién está con usted?",
    find_button: "Mostrar programas",
    switch_lang: "English",
    switch_lang_alt: "Switch to English",
    crisis_title: "Si usted o alguien con usted está en peligro esta noche",
    handoffs_title: "Cómo obtener ayuda de verdad",
    handoff_call: "Llamar",
    handoff_web: "Sitio web",
    programs_title: "Programas que pueden encajar",
    empty_msg: "No encontramos un programa listado para esta situación exacta. Eso no significa que no haya ayuda — las rutas de arriba son la forma más rápida de comenzar.",
    who_it_serves: "A quién ayuda",
    what_pays: "Qué paga",
    wait: "Cuánto suele esperar la gente",
    documents: "Traiga esto si puede",
    action_label: "Conectarse",
    action_help: "Llame al número de arriba, o pida una referencia a Entrada Coordinada. No necesita llenar nada en este sitio para recibir ayuda.",
    empty_situation: "No estoy seguro — muéstreme lo que existe",
    situations: {
      veteran: "Veterano/a",
      recovery: "Saliendo de tratamiento o en recuperación",
      reentry: "Saliendo de la cárcel o prisión",
      dv: "Huyendo de violencia en casa",
      disability: "Viviendo con una discapacidad",
      youth: "Joven adulto (18–24) sin vivienda estable",
      senior: "Adulto mayor (55+ o 62+)",
      priced_out: "Trabajo pero no puedo pagar la renta",
      not_sure: "No estoy seguro",
    },
    households: {
      just_me: "Solo yo",
      me_children: "Yo y mis hijos",
      couple: "Una pareja",
      larger: "Hogar más grande",
    },
    provider_title: "Tengo una unidad. ¿Qué podría ser?",
    provider_lede: "Vea qué programas podrían usar su propiedad — sin listarla, publicarla, ni crear una cuenta. Nada se comparte hasta que usted lo diga.",
    provider_address: "Dirección de la propiedad",
    provider_zip: "Código postal (opcional)",
    provider_beds: "Habitaciones",
    provider_baths: "Baños",
    provider_sqft: "Pies cuadrados",
    provider_occupied: "¿Actualmente ocupada?",
    provider_submit: "Mostrar programas que encajan",
    owner_form_asking_about: "Preguntando sobre",
    provider_fits_title: "Programas que podrían usar esta propiedad",
    provider_must_be_true: "Qué tendría que ser cierto",
    provider_next: "Hablar con Throughline",
    owner_next_heading: "¿Quiere el panorama completo para esta propiedad?",
    owner_next_body: "Una persona revisará esta propiedad específica y le enviará lo que cada opción realmente pagaría, qué necesitaría la propiedad y cómo se compara con rentarla de manera convencional. La respuesta suele ser dentro de dos días hábiles.",
    owner_next_button: "Obtener el panorama completo",
    owner_form_title: "Envíe su propiedad a Throughline",
    owner_form_property: "Para",
    owner_form_name: "Su nombre",
    owner_form_email: "Correo electrónico",
    owner_form_email_help: "Aquí enviamos un enlace de confirmación de un solo uso; luego una persona le responde a esta dirección.",
    owner_err_email: "Ingrese un correo electrónico válido — así confirmamos y le respondemos.",
    owner_err_name: "Escriba su nombre.",
    owner_form_phone: "Teléfono (opcional)",
    owner_form_org: "Organización (opcional)",
    owner_form_notes: "¿Algo más sobre la propiedad? (opcional)",
    owner_form_submit: "Enviar",
    owner_sent: "Gracias — tenemos su propiedad. Revise su correo electrónico para confirmar rápidamente, luego una persona la revisará y responderá dentro de dos días hábiles. Su propiedad no ha sido listada ni publicada.",
    privacy_note: "Su propiedad no se lista, no se publica, no es visible para otros. Nada se comparte sin usted.",
    footer_compliance: "Throughline es infraestructura de información. No está afiliada con HUD, VA, ni el Condado de Bexar. No es una solicitud, lista de espera, ni intake.",
  },
};
